import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { utils, writeFile } from 'xlsx';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const GclidDownloads = () => {
  const [conversations, setConversations] = useState([]);
  const [tagsMap, setTagsMap] = useState(new Map());
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }).slice(0, 7) // Default to current month in Mexico City time
  );
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [filterTags, setFilterTags] = useState({ include: [], exclude: [] });

  useEffect(() => {
    const fetchData = async () => {
      const conversationsRef = collection(db, 'conversations');
      const tagsRef = collection(db, 'tags');

      const [conversationsSnapshot, tagsSnapshot] = await Promise.all([
        getDocs(conversationsRef),
        getDocs(tagsRef),
      ]);

      setConversations(conversationsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      const newTagsMap = new Map();
      tagsSnapshot.docs.forEach((doc) => {
        newTagsMap.set(doc.id, doc.data().tags || []);
      });
      setTagsMap(newTagsMap);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const selectedYear = selectedMonth.split('-')[0];
    const selectedMonthIndex = parseInt(selectedMonth.split('-')[1]) - 1;

    const filteredData = conversations.filter(({ id, timestamp }) => {
      const conversationDate = new Date(timestamp.seconds * 1000);
      const isInSelectedMonth =
        conversationDate.getFullYear() === parseInt(selectedYear) &&
        conversationDate.getMonth() === selectedMonthIndex;

      const conversationTags = tagsMap.get(id) || [];

      const hasIncludedTags = filterTags.include.every((tag) => conversationTags.includes(tag));
      const hasExcludedTags = filterTags.exclude.some((tag) => conversationTags.includes(tag));

      return isInSelectedMonth && hasIncludedTags && !hasExcludedTags;
    });

    // Sort by conversation ID (id_simple) in descending order
    const sortedData = filteredData.sort((a, b) => (b.id_simple || 0) - (a.id_simple || 0));
    setFilteredConversations(sortedData);
  }, [selectedMonth, conversations, tagsMap, filterTags]);

  const handleDownloadExcel = () => {
    const ws = utils.json_to_sheet(
      filteredConversations.map((record) => {
        const { id, id_simple, gclid } = record;
        const conversationTags = tagsMap.get(id) || [];
        return {
          'ID': id_simple || '',
          'Quote ID': id,
          Gclid: gclid || '',
          Tags: conversationTags.join(', '),
        };
      })
    );
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Gclid Stats');
    writeFile(wb, `Gclid_Stats_${selectedMonth}.xlsx`);
  };

  const handleTagChange = (e) => {
    const { name, value } = e.target;
    const isChecked = e.target.checked;

    setFilterTags((prev) => {
      const updatedTags = isChecked
        ? [...prev[name], value]
        : prev[name].filter((tag) => tag !== value);
      return { ...prev, [name]: updatedTags };
    });
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-4xl mx-auto">
      <h3 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">Gclid Downloads</h3>

      <div className="flex items-center space-x-4 mb-6">
        <div>
          <label className="block text-gray-700 text-md font-medium mb-2">Select Month:</label>
          <input
            type="month"
            className="p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </div>

        <button
          onClick={handleDownloadExcel}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex mt-7 items-center"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="mb-4">
        <h4 className="font-bold text-lg mb-2">Filter Tags:</h4>
        <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
          <div>
            <label className="font-medium">Include:</label>
            <div className="flex flex-wrap gap-2">
              {['internal', 'internal_check', 'WA', 'cotiza', 'pregunta', 'profanity', 'E_precio', 'E_API', 'E_auto', 'E_eval', 'E_RAG', 'seguridad', 'extraordinario',
                'Uber', 'Plataforma', 'Particular', 'finalizado'
              ].map((tag) => (
                <div key={`include-${tag}`} className="flex items-center space-x-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    name="include"
                    value={tag}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    onChange={handleTagChange}
                  />
                  <label className="text-sm font-medium text-gray-700">{tag}</label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="font-medium">Exclude:</label>
            <div className="flex flex-wrap gap-2">
              {['internal', 'internal_check', 'WA', 'cotiza', 'pregunta', 'profanity', 'E_precio', 'E_API', 'E_auto', 'E_eval', 'E_RAG', 'seguridad', 'extraordinario',
                'Uber', 'Plataforma', 'Particular', 'finalizado'
              ].map((tag) => (
                <div key={`exclude-${tag}`} className="flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <input
                    type="checkbox"
                    name="exclude"
                    value={tag}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    onChange={handleTagChange}
                  />
                  <label className="text-sm font-medium text-gray-700">{tag}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Conversations Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <thead>
            <tr className="bg-gray-100 text-gray-800 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left">ID</th>
              <th className="py-3 px-6 text-left">Quote ID</th>
              <th className="py-3 px-6 text-left">Gclid</th>
              <th className="py-3 px-6 text-left">Tags</th>
            </tr>
          </thead>
          <tbody className="text-gray-700 text-sm font-light">
            {filteredConversations.map((record, index) => {
              const conversationTags = tagsMap.get(record.id) || [];
              return (
                <tr key={`${record.id}-${index}`} className="border-b border-gray-200 hover:bg-gray-100">
                  <td className="py-3 px-6">{record.id_simple || '--'}</td>
                  <td className="py-3 px-6" title={record.id}>{record.id ? `${record.id.substring(0, 30)}...` : '--'}</td>
                  <td className="py-3 px-6" title={record.gclid}>{record.gclid ? `${record.gclid.substring(0, 30)}...` : '--'}</td>
                  <td className="py-3 px-6">{conversationTags.length > 0 ? conversationTags.join(', ') : '--'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GclidDownloads;